/**
 * @author        calvin@zumper.com (Calvin Yau)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   getCurrentPosition Thunks
 */

import { ButterAction } from '@zcode-ui/web-zumper-global-state'
import { type AutocompleteCurrentPositionResult, AutocompleteResult } from '@zcode/bizos'
import {
  AnalyticsC,
  PLEASE_ALLOW_GEOLOCATION,
  UNSUPPORTED_BROWSER_GEOLOCATION,
  trackGAEvent,
} from '@zcode/nr-utils'
import type { GetState } from '@zcode/state-type'
import type { Dispatch } from 'redux'
import invariant from 'tiny-invariant'
import { logger } from '../../../../../logger'
import { setIsCurrentLocationLoading } from '../../../../../shared/modules/autocomplete/actions'
import { selectIsCurrentLocationLoading } from '../../../../../shared/modules/autocomplete/selectors'
import type { AutocompleteOrigin } from '../../constants'

interface GetCurrentPositionProps {
  onSelectAutocompleteResult: (
    result: AutocompleteCurrentPositionResult,
    isCurrentLocation?: true
  ) => Promise<void> | void
  onError?: (error: { message: string }) => void
  origin?: AutocompleteOrigin
}

const getCurrentPosition =
  ({ onSelectAutocompleteResult, onError, origin }: GetCurrentPositionProps) =>
  (dispatch: Dispatch, getState: GetState) => {
    let state = getState()
    let isCurrentLocationLoading = selectIsCurrentLocationLoading(state)

    if (typeof window === 'undefined' || !window.navigator || !window.navigator.geolocation) {
      trackGAEvent(AnalyticsC.GEO_LOCATION, 'error', origin)
      onError?.({ message: UNSUPPORTED_BROWSER_GEOLOCATION })
      return
    }

    trackGAEvent(AnalyticsC.GEO_LOCATION, 'click', origin)

    if (!isCurrentLocationLoading) {
      dispatch(setIsCurrentLocationLoading(true))
    }

    return window.navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords
        const result = new AutocompleteResult({ lat, lng, record_type: 'current_position' })
        invariant(result.type === 'current_position')
        try {
          await onSelectAutocompleteResult(result)
          const state = getState()
          isCurrentLocationLoading = selectIsCurrentLocationLoading(state)
          if (isCurrentLocationLoading) {
            dispatch(setIsCurrentLocationLoading(false))
          }
          trackGAEvent(AnalyticsC.GEO_LOCATION, 'success', origin)
        } catch (e) {
          logger.error('autocomplete: geos get failed', { lat, lng }, e)
        }
      },
      (error) => {
        // checking for PERMISSION_DENIED error here https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError/code
        const errorMessage =
          error.code === 1
            ? 'There is no location support on this device or it is disabled. Please check your settings.'
            : 'There was an error using your location. Please try again.'
        dispatch(
          // @ts-expect-error
          ButterAction.addNotifyButterBar({
            duration: ButterAction.BUTTER_DURATION_A11Y_COMPLIANT,
            color: ButterAction.INFO_COLOR,
            message: errorMessage,
            isMessageButter: true,
          })
        )
        state = getState()
        isCurrentLocationLoading = selectIsCurrentLocationLoading(state)
        if (isCurrentLocationLoading) {
          dispatch(setIsCurrentLocationLoading(false))
        }
        trackGAEvent(AnalyticsC.GEO_LOCATION, 'error', origin)
        const rewordedMessage =
          error.code === error.PERMISSION_DENIED ? PLEASE_ALLOW_GEOLOCATION : ''
        onError?.({
          message: rewordedMessage || `Unknown error: ${error.message}`,
        })
      }
    )
  }

export default getCurrentPosition
